import { createReducer, Action, on } from '@ngrx/store';
import { first } from 'lodash-es';

import { DealActions } from './deal.actions';
import { DealDiffStatus, DealState } from './deal.state';
import { DocumentCompareMode } from '../../features/document-viewer/models/document-viewer.models';
import { measureReducer } from '../../features/shared/utils/runtime-reducer-stats';
import { EmbeddedActions } from '../embedded/embedded.actions';

export const initialState: DealState = {
  loading: true,
  identifiersLoading: true,
  compareMode: DocumentCompareMode.Normal,
  isCompare: false,
  diffStatus: DealDiffStatus.Selection,
};

const featureReducer = measureReducer(
  createReducer(
    initialState,
    on(DealActions.Reset, (state): DealState => initialState),
    on(DealActions.LoadDeal, (state, action): DealState => {
      const isSameTransaction =
        state.transaction?.transactionId === action.transaction?.transactionId;

      const compareState: Partial<DealState> = isSameTransaction
        ? {
            originalDocumentId: state.originalDocumentId,
            originalDocumenTransactionLibrarytId: state.originalDocumenTransactionLibrarytId,
            isCompare: state.isCompare,
          }
        : {
            originalDocumentId: undefined,
            originalDocumenTransactionLibrarytId: undefined,
            isCompare: false,
          };

      return {
        ...state,
        loading: true,
        identifiersLoading: true,
        error: undefined,
        deal: undefined,
        diffUrl: undefined,
        identifiers: undefined,
        transaction: action.transaction,
        ...compareState,
      };
    }),
    on(
      DealActions.LoadDealSuccess,
      (state, action): DealState => ({
        ...state,
        loading: false,
        deal: action.deal,
      })
    ),
    on(
      DealActions.RequestLoad,
      (state, action): DealState => ({
        ...state,
        hasDocument:
          action.transaction &&
          (!!action.transaction.documents.find(
            (d) => d.documentId === action.transaction.firstDisplayDocument
          ) ||
            !!action.transaction.documents.find((d) => d.documentId.endsWith('-oc')) ||
            !!first(action.transaction.documents)),
      })
    ),
    on(
      EmbeddedActions.LoadDealSuccess,
      (state, action): DealState => ({
        ...state,
        loading: false,
        deal: action.deal,
        transaction: action.deal,
      })
    ),
    on(
      DealActions.LoadDealFail,
      DealActions.LoadDealDiffFail,
      DealActions.LoadAvailableDealDiffListFail,
      (state, action): DealState => ({ ...state, loading: false, error: action.error })
    ),
    on(
      DealActions.LoadIdentifiersSuccess,
      (state, action): DealState => ({
        ...state,
        identifiersLoading: false,
        identifiers: action.identifiers,
      })
    ),
    on(
      DealActions.LoadIdentifiersFail,
      (state, action): DealState => ({ ...state, identifiersLoading: false, error: action.error })
    ),
    on(
      DealActions.SetOriginalDocumentId,
      (state, action): DealState => ({
        ...state,
        originalDocumentId: action.originalDocumentId,
        originalDocumenTransactionLibrarytId: action.originalDocumentTransactionLibraryId,
      })
    ),
    on(
      DealActions.SetCompareMode,
      (state, action): DealState => ({ ...state, compareMode: action.compareMode })
    ),
    on(
      DealActions.SetIsCompare,
      (state, action): DealState => ({
        ...state,
        isCompare: action.isCompare,
        diffStatus: DealDiffStatus.Selection,
      })
    ),
    on(DealActions.LoadDealDiff, (state, action): DealState => ({ ...state, diffUrl: undefined })),
    on(
      DealActions.LoadDealDiffSuccess,
      (state, action): DealState => ({ ...state, diffUrl: action.diffUrl })
    ),
    on(
      DealActions.StartLoadDealDiff,
      (state, action): DealState => ({ ...state, diffStatus: DealDiffStatus.Loading })
    ),
    on(
      DealActions.TriggerDealDiff,
      (state, action): DealState => ({ ...state, diffStatus: DealDiffStatus.Generating })
    ),
    on(
      DealActions.LoadAvailableDealDiffListSuccess,
      (state, action): DealState => ({ ...state, availableDiffs: action.availableDiffs })
    ),
    on(
      DealActions.SetStatus,
      (state, action): DealState => ({ ...state, diffStatus: action.diffStatus })
    )
  )
);

export function reducer(state: DealState | undefined, action: Action) {
  return featureReducer(state, action);
}
